import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useProvider } from '../contexts/ProviderContext'
import { useToast } from '../contexts/ToastContext'
import { supabase } from '../lib/supabase'
import { ArrowRightIcon, CheckCircleIcon, KeyIcon } from '@heroicons/react/24/outline'
import GoogleSheetsOnboarding from './GoogleSheetsOnboarding'

type ProviderType = 'google_sheets' | 'manual_spreadsheet'

const ProviderOnboarding: React.FC = () => {
  const { user } = useAuth()
  const { setCurrentProviderId, refreshProviders } = useProvider()
  const toast = useToast()
  const navigate = useNavigate()

  // Token state
  const [inviteToken, setInviteToken] = useState('')
  const [tokenValidated, setTokenValidated] = useState(false)
  const [tokenValidating, setTokenValidating] = useState(false)

  const [currentStep, setCurrentStep] = useState(() => {
    // Restore state after OAuth redirect
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const savedStep = sessionStorage.getItem('provider_onboarding_step')
    const savedToken = sessionStorage.getItem('provider_onboarding_token')
    if (code && savedStep && savedToken) {
      return parseInt(savedStep, 10)
    }
    return 0 // Start at token entry step
  })
  const [loading, setLoading] = useState(false)
  const [selectedType, setSelectedType] = useState<ProviderType | null>(() => {
    // Restore type after OAuth redirect
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const savedType = sessionStorage.getItem('provider_onboarding_type')
    if (code && savedType) {
      return savedType as ProviderType
    }
    return null
  })

  // Manual spreadsheet form data
  const [providerName, setProviderName] = useState('')

  // Restore token validation state from sessionStorage (for OAuth redirect)
  useEffect(() => {
    const savedToken = sessionStorage.getItem('provider_onboarding_token')
    if (savedToken) {
      setInviteToken(savedToken)
      setTokenValidated(true)
    }

    // Check for token in URL params (from invite link)
    const params = new URLSearchParams(window.location.search)
    const urlToken = params.get('token')
    if (urlToken && !savedToken) {
      setInviteToken(urlToken)
      // Auto-validate token from URL
      validateToken(urlToken)
    }
  }, [])

  const validateToken = async (tokenToValidate?: string) => {
    const token = tokenToValidate || inviteToken.trim()
    if (!token) {
      toast.error('Please enter an invite token')
      return
    }

    setTokenValidating(true)

    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        'provider-onboarding-token-validate',
        { body: { token } }
      )

      if (invokeError) throw invokeError

      if ((data as any)?.valid) {
        setTokenValidated(true)
        setInviteToken(token)
        sessionStorage.setItem('provider_onboarding_token', token)
        setCurrentStep(1)
        sessionStorage.setItem('provider_onboarding_step', '1')
      } else {
        toast.error((data as any)?.error || 'Invalid invite token')
      }
    } catch (err: any) {
      console.error('Token validation error:', err)
      toast.error(err.message || 'Failed to validate invite token')
    } finally {
      setTokenValidating(false)
    }
  }

  const consumeToken = async (providerId: string) => {
    const token = sessionStorage.getItem('provider_onboarding_token') || inviteToken
    if (!token) {
      console.warn('consumeToken: no token found in sessionStorage or state — skipping')
      return
    }

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('provider-onboarding-token-consume', {
        body: { token, provider_id: providerId },
      })

      if (invokeError) {
        console.error('Token consumption invoke error (non-fatal):', invokeError)
      } else if ((data as any)?.error) {
        console.error('Token consumption API error (non-fatal):', (data as any).error)
      } else {
        console.log('Token consumed successfully, auto_connected:', (data as any)?.auto_connected)
      }
    } catch (err) {
      console.error('Token consumption error (non-fatal):', err)
    }
  }

  const handleTypeSelection = (type: ProviderType) => {
    setSelectedType(type)
    setCurrentStep(2)
    // Save state for OAuth redirect restoration
    sessionStorage.setItem('provider_onboarding_step', '2')
    sessionStorage.setItem('provider_onboarding_type', type)
  }

  const handleManualSpreadsheetSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (!providerName.trim()) {
        throw new Error('Please enter a provider name')
      }

      // Create manual spreadsheet provider
      // Include onboarding token so deploy_provider can consume it server-side
      const onboardingToken = sessionStorage.getItem('provider_onboarding_token') || inviteToken || undefined
      const { data: deployData, error: deployError } = await supabase.functions.invoke('deploy_provider', {
        body: {
          provider_type: 'spreadsheet',
          provider_name: providerName.trim(),
          onboarding_token: onboardingToken,
        },
      })

      if (deployError) throw deployError
      if (!deployData?.provider_id) throw new Error('Failed to create provider')

      // Consume the onboarding token
      await consumeToken(deployData.provider_id)

      // Ensure auth_users_providers entry exists (deploy_provider may have already created it)
      const { error: authError } = await supabase
        .from('auth_users_providers')
        .upsert({
          auth_user_id: user?.id,
          provider_id: deployData.provider_id,
        }, { onConflict: 'auth_user_id,provider_id' })

      if (authError) throw authError

      // Set as current provider and refresh
      setCurrentProviderId(deployData.provider_id)
      await refreshProviders()

      // Clear onboarding state from sessionStorage
      sessionStorage.removeItem('provider_onboarding_step')
      sessionStorage.removeItem('provider_onboarding_type')
      sessionStorage.removeItem('provider_onboarding_token')

      setCurrentStep(3)
    } catch (err: any) {
      console.error('Error creating manual spreadsheet provider:', err)
      toast.error(err.message || 'Failed to create provider. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSheetsSuccess = async (providerId: string) => {
    try {
      // Consume the onboarding token
      await consumeToken(providerId)

      // Ensure auth_users_providers entry exists (deploy_provider may have already created it)
      const { error: authError } = await supabase
        .from('auth_users_providers')
        .upsert({
          auth_user_id: user?.id,
          provider_id: providerId,
        }, { onConflict: 'auth_user_id,provider_id' })

      if (authError) {
        console.error('Error creating provider access:', authError)
        // Don't throw - provider is created, just log the error
      }

      // Set as current provider and refresh
      setCurrentProviderId(providerId)
      await refreshProviders()

      // Clear onboarding state from sessionStorage
      sessionStorage.removeItem('provider_onboarding_step')
      sessionStorage.removeItem('provider_onboarding_type')
      sessionStorage.removeItem('provider_onboarding_token')

      setCurrentStep(3)
    } catch (err: any) {
      console.error('Error finalizing provider setup:', err)
      toast.error(err.message || 'Provider created but failed to finalize setup')
    }
  }

  const handleComplete = () => {
    navigate('/provider/catalog')
  }

  // Steps: 0=Token, 1=Type, 2=Setup, 3=Success
  const totalSteps = 4
  const stepLabels = ['Invite', 'Type', 'Setup', 'Done']

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Logo/Brand Section */}
        <div className="text-center mb-8">
          <div className="inline-block">
            <div className="w-16 h-16 mx-auto bg-orange-500 flex items-center justify-center  mb-4">
              <span className="text-2xl font-bold text-white ">FS</span>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white ">
            PROVIDER ONBOARDING
          </h1>
          <p className="text-white/50 text-sm  mt-2">
            Set up your product provider account
          </p>
          <div className="w-20 h-1 bg-orange-500 mx-auto mt-4"></div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {Array.from({ length: totalSteps }, (_, i) => (
            <React.Fragment key={i}>
              <div className="flex flex-col items-center">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                    currentStep >= i
                      ? 'border-orange-500 bg-orange-500 text-white'
                      : 'border-white/15  text-white/35'
                  }  font-bold`}
                >
                  {currentStep > i ? '\u2713' : i + 1}
                </div>
                <span className={`text-xs mt-1 ${currentStep >= i ? 'text-orange-400' : 'text-white/25'}`}>
                  {stepLabels[i]}
                </span>
              </div>
              {i < totalSteps - 1 && (
                <div
                  className={`w-16 h-1 mx-1 mb-5 ${
                    currentStep > i ? 'bg-orange-500' : 'bg-gray-300'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Main Content Card */}
        <div className="glass-card rounded-2xl p-8">
          {/* Step 0: Enter Invite Token */}
          {currentStep === 0 && (
            <div>
              <div className="flex justify-center mb-6">
                <KeyIcon className="w-16 h-16 text-orange-500" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2 text-center">
                ENTER INVITE CODE
              </h2>
              <p className="text-white/50 text-sm mb-6 text-center">
                You need an invite code from a merchant or admin to set up a provider account
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-white/70 mb-2">
                    INVITE CODE *
                  </label>
                  <input
                    type="text"
                    value={inviteToken}
                    onChange={(e) => setInviteToken(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') validateToken() }}
                    placeholder="Paste your invite code here"
                    className="w-full px-4 py-3 border border-white/[0.08] text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-white bg-transparent"
                    autoFocus
                  />
                  <p className="text-xs text-white/35 mt-1">
                    Contact a merchant or platform admin if you don't have an invite code
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <button
                  onClick={() => validateToken()}
                  disabled={tokenValidating || !inviteToken.trim()}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {tokenValidating ? 'VALIDATING...' : 'CONTINUE'}
                </button>
              </div>
            </div>
          )}

          {/* Step 1: Choose Provider Type */}
          {currentStep === 1 && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-2 ">
                SELECT PROVIDER TYPE
              </h2>
              <p className="text-white/50 text-sm  mb-6">
                Choose how you want to manage your product catalog
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Google Sheets Option */}
                <button
                  onClick={() => handleTypeSelection('google_sheets')}
                  className="group relative  hover:bg-orange-500/10 border-2 border-white/[0.08] hover:border-orange-500 p-6 transition-all duration-200  hover:shadow-lg text-left rounded-2xl"
                >
                  <div className="flex items-start">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white  mb-2">
                        GOOGLE SHEETS
                      </h3>
                      <p className="text-sm text-white/50  mb-4">
                        Sync products from your Google Spreadsheet
                      </p>
                      <ul className="text-xs text-white/35  space-y-1">
                        <li>• Automatic sync</li>
                        <li>• OAuth secured</li>
                        <li>• Easy updates</li>
                      </ul>
                    </div>
                    <ArrowRightIcon className="w-6 h-6 text-white/25 group-hover:text-orange-400 transition-colors" />
                  </div>
                </button>

                {/* Manual Spreadsheet Option */}
                <button
                  onClick={() => handleTypeSelection('manual_spreadsheet')}
                  className="group relative  hover:bg-orange-500/10 border-2 border-white/[0.08] hover:border-orange-500 p-6 transition-all duration-200  hover:shadow-lg text-left rounded-2xl"
                >
                  <div className="flex items-start">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white  mb-2">
                        MANUAL ENTRY
                      </h3>
                      <p className="text-sm text-white/50  mb-4">
                        Manage products directly in the admin panel
                      </p>
                      <ul className="text-xs text-white/35  space-y-1">
                        <li>• Simple setup</li>
                        <li>• Manual control</li>
                        <li>• Web interface</li>
                      </ul>
                    </div>
                    <ArrowRightIcon className="w-6 h-6 text-white/25 group-hover:text-orange-400 transition-colors" />
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Provider-Specific Setup */}
          {currentStep === 2 && (
            <div>
              {selectedType === 'google_sheets' ? (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2 ">
                    CONNECT GOOGLE SHEETS
                  </h2>
                  <p className="text-white/50 text-sm  mb-6">
                    Authorize access to your Google Sheets
                  </p>
                  <GoogleSheetsOnboarding
                    onSuccess={handleGoogleSheetsSuccess}
                    onCancel={() => setCurrentStep(1)}
                  />
                </div>
              ) : (
                <form onSubmit={handleManualSpreadsheetSubmit}>
                  <h2 className="text-2xl font-bold text-white mb-2 ">
                    PROVIDER DETAILS
                  </h2>
                  <p className="text-white/50 text-sm  mb-6">
                    Set up your manual spreadsheet provider
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-bold text-white/70 mb-2 ">
                        PROVIDER NAME *
                      </label>
                      <input
                        type="text"
                        value={providerName}
                        onChange={(e) => setProviderName(e.target.value)}
                        placeholder="e.g., My Product Warehouse"
                        required
                        className="w-full px-4 py-3 border border-white/[0.08]  text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent "
                      />
                      <p className="text-xs text-white/35  mt-1">
                        Choose a name that identifies your product source
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 mt-6">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      className="flex-1 glass-card rounded-xl text-white/70  font-semibold py-3 px-6 hover:bg-white/[0.08] transition-colors "
                    >
                      BACK
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white  font-semibold py-3 px-6 transition-colors  disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'CREATING...' : 'CONTINUE'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Step 3: Success */}
          {currentStep === 3 && (
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <CheckCircleIcon className="w-20 h-20 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2 ">
                SETUP COMPLETE
              </h2>
              <p className="text-white/50  mb-8">
                Your provider account has been successfully created!
              </p>

              <button
                onClick={handleComplete}
                className="bg-orange-500 hover:bg-orange-600 text-white  font-semibold py-3 px-8 transition-colors "
              >
                GO TO CATALOG
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProviderOnboarding
